import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import type { Passkey, PasskeyCreationOptions, SessionState, StepUpPurpose } from '@chapsi/contracts';
import { ApiError, api } from '../api/client.js';

/**
 * Passkeys in the browser (docs/adr/0026-passkeys.md), with @simplewebauthn/browser. The API sends the
 * standard WebAuthn JSON; these helpers hand it to the browser and send the result back.
 *
 * The library loads on first use rather than with the app's first screen: everyone signed in downloads
 * the entry chunk, and only a passkey ceremony needs it.
 */
type WebAuthnLib = typeof import('@simplewebauthn/browser');
let loaded: WebAuthnLib | null = null;
let loading: Promise<WebAuthnLib> | null = null;
function webauthn(): Promise<WebAuthnLib> {
  loading ??= import('@simplewebauthn/browser').then((lib) => (loaded = lib));
  return loading;
}

/**
 * The same check the library makes (`browserSupportsWebAuthn`), without loading it. Screens that offer
 * passkeys call this as they render, so it also starts fetching the library: by the time someone presses
 * a passkey button it is there, and the browser prompt opens straight from the click.
 */
export function passkeysSupported(): boolean {
  try {
    const supported = typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function';
    if (supported && !loading) void webauthn().catch(() => (loading = null));
    return supported;
  } catch {
    return false;
  }
}

export async function autofillSupported(): Promise<boolean> {
  try {
    return passkeysSupported() && (await (loaded ?? (await webauthn())).browserSupportsWebAuthnAutofill());
  } catch {
    return false;
  }
}

/** The person closed the browser's passkey sheet, it timed out, or another request replaced it. */
export function wasCancelled(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name;
  const code = (err as { code?: string } | null)?.code;
  return name === 'NotAllowedError' || name === 'AbortError' || code === 'ERROR_CEREMONY_ABORTED';
}

/** Words for a failed passkey attempt: the API's own message, or the browser's reason in plain terms. */
export function passkeyErrorMessage(err: unknown, fallback = 'That passkey did not work. Try again.'): string {
  if (err instanceof ApiError) return err.message;
  if (wasCancelled(err)) return 'The passkey request was cancelled. Try again when you are ready.';
  const name = (err as { name?: string } | null)?.name;
  if (name === 'InvalidStateError') return 'This device already has a passkey for your account.';
  if (name === 'NotSupportedError') return 'This browser or device does not support passkeys.';
  if (name === 'SecurityError') return 'Passkeys are not available on this address.';
  return fallback;
}

/** Stops any passkey prompt still open, such as the sign-in field's autofill request. */
export function cancelPasskeyPrompt(): void {
  // No library yet means no ceremony was ever started, so there is nothing to stop.
  loaded?.WebAuthnAbortService.cancelCeremony();
}

/**
 * Signs in with a passkey. `autofill` waits for the person to pick one from the email field's
 * suggestions (conditional UI) instead of opening the browser's sheet straight away.
 */
export async function signInWithPasskey(autofill = false): Promise<SessionState> {
  const { challengeId, options } = await api('passkeySignInOptions');
  const response = await (loaded ?? (await webauthn())).startAuthentication({
    optionsJSON: options as unknown as PublicKeyCredentialRequestOptionsJSON,
    useBrowserAutofill: autofill,
  });
  return api('passkeySignIn', { body: { challengeId, response: plain(response) } });
}

/**
 * Adding a passkey needs a fresh second factor: a sign-in within the last 10 minutes, or the six-digit code
 * from `requestPasskeyAddCode`. Without either the API answers `reauth_required`; see `needsReauth`.
 */
export function needsReauth(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'reauth_required';
}

/** Emails the person a six-digit code for adding a passkey. */
export function requestPasskeyAddCode(): Promise<{ expiresAt: string }> {
  return api('requestPasskeyAddCode');
}

/**
 * Registration options, authorised by a recent sign-in or `code` (which this spends). They stay good for
 * five minutes, so a cancelled browser prompt can be retried with the same options and no new code.
 */
export async function passkeyAddOptions(code?: string): Promise<PasskeyCreationOptions> {
  return api('passkeyRegisterOptions', { body: code ? { code } : {} });
}

/** Creates a passkey on this device for the given options and adds it to the signed-in person's account. */
export async function createPasskey(options: PasskeyCreationOptions, name?: string): Promise<Passkey> {
  const response = await (loaded ?? (await webauthn())).startRegistration({ optionsJSON: options as unknown as PublicKeyCredentialCreationOptionsJSON });
  const trimmed = name?.trim();
  return api('passkeyRegister', { body: { response: plainRegistration(response), ...(trimmed ? { name: trimmed } : {}) } });
}

/** Creates a passkey on this device and adds it to the signed-in person's account. */
export async function addPasskey(name?: string, code?: string): Promise<Passkey> {
  return createPasskey(await passkeyAddOptions(code), name);
}

/** Proves this browser's second factor with a passkey after an email sign-in (docs/adr/0033). */
export async function verifySecondFactorWithPasskey(): Promise<SessionState> {
  const { challengeId, options } = await api('secondFactorPasskeyOptions');
  const response = await (loaded ?? (await webauthn())).startAuthentication({ optionsJSON: options as unknown as PublicKeyCredentialRequestOptionsJSON });
  return api('verifySecondFactor', { body: { method: 'passkey', challengeId, response: plain(response) } });
}

/** Confirms a sensitive action with a passkey; returns the one-time code the action accepts. */
export async function stepUpWithPasskey(purpose: StepUpPurpose): Promise<{ code: string; expiresAt: string }> {
  const { challengeId, options } = await api('passkeyStepUpOptions', { body: { purpose } });
  const response = await (loaded ?? (await webauthn())).startAuthentication({ optionsJSON: options as unknown as PublicKeyCredentialRequestOptionsJSON });
  return api('passkeyStepUp', { body: { challengeId, response: plain(response) } });
}

/**
 * Confirms deleting your own account with a passkey (docs/adr/0040-account-deletion.md). Returns the step-up the
 * schedule call sends; the API verifies it there, once.
 */
export async function accountDeletionPasskeyStepUp(): Promise<{ method: 'passkey'; challengeId: string; response: ReturnType<typeof plain> }> {
  const { challengeId, options } = await api('accountDeletionPasskeyOptions');
  const response = await (loaded ?? (await webauthn())).startAuthentication({ optionsJSON: options as unknown as PublicKeyCredentialRequestOptionsJSON });
  return { method: 'passkey', challengeId, response: plain(response) };
}

/**
 * The browser library's response interfaces as plain object literals (spread copies), which the contract's
 * open schemas accept. Nothing is added or dropped.
 */
function plain(r: AuthenticationResponseJSON) {
  return { ...r, response: { ...r.response }, clientExtensionResults: { ...r.clientExtensionResults } };
}
function plainRegistration(r: RegistrationResponseJSON) {
  return { ...r, response: { ...r.response }, clientExtensionResults: { ...r.clientExtensionResults } };
}

// --- "Sign in faster next time" offer after a magic-link sign-in ----------------------------------

const OFFER_FLAG = 'chapsi.passkey-offer';
const dismissedKey = (userId: string) => `chapsi.passkey-offer-dismissed.${userId}`;

/** Called after a magic-link sign-in: the app may offer a passkey for the rest of this browser session. */
export function markMagicLinkSignIn(): void {
  try {
    sessionStorage.setItem(OFFER_FLAG, '1');
  } catch {
    // Storage blocked: no offer, nothing else changes.
  }
}

export function shouldOfferPasskey(userId: string): boolean {
  try {
    return sessionStorage.getItem(OFFER_FLAG) === '1' && localStorage.getItem(dismissedKey(userId)) !== '1';
  } catch {
    return false;
  }
}

export function dismissPasskeyOffer(userId: string): void {
  try {
    localStorage.setItem(dismissedKey(userId), '1');
    sessionStorage.removeItem(OFFER_FLAG);
  } catch {
    // Storage blocked: the card still closes for now.
  }
}
